import { useState, type FormEvent } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { CheckCheck, FolderOpen, Hand, Search, UserCheck, UserPlus } from 'lucide-react'
import {
  APPLICATION_STATE_LABELS,
  DECISION_KINDS,
  PAYMENT_STATE_LABELS,
  PAYMENT_STATES,
  type ApplicationState,
  type DecisionKind,
  type PaymentState,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  Empty,
  Input,
  MatriculeTag,
  PageHeader,
  Pagination,
  SegmentedPills,
  Select,
  StatusBadge,
  Table,
  useAuth,
  useCan,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when, type Competition } from '../../lib/competition-types'
import type { Paginated } from '../../lib/rbac-types'

export const APPLICATIONS_KEY = ['admin', 'applications'] as const

export interface AdminApplicationRow {
  id: string
  applicationNumber: string
  status: ApplicationState
  statusLabel: string
  paymentStatus: PaymentState
  paymentStatusLabel: string
  category: string | null
  submittedAt: string | null
  createdAt: string | null
  candidate: { lastName: string | null; firstName: string | null; userNumber: string | null }
  competition: { id: string; reference: string; title: string }
  assignee: { id: string; name: string } | null
  pendingDecision: { decision: DecisionKind; bySystem: boolean } | null
  complementDueAt: string | null
}

interface BulkResult {
  results: { id: string; ok: boolean; error?: string }[]
  done: number
}

/** Filtres : dossiers déposés d'abord (un brouillon ne vaut pas dépôt). */
const FILTERS: (ApplicationState | 'all')[] = [
  'submitted',
  'under_review',
  'to_complete',
  'admissible',
  'not_admissible',
  'draft',
  'withdrawn',
  'all',
]

/**
 * Dossiers de candidature (phase 9 : consultation, versions, corrections). Toujours
 * limités aux concours de la portée de l'agent (filtré par l'API).
 */
export function ApplicationsPage() {
  const { session, user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canReview = useCan('candidature.review')
  const canAssign = useCan('candidature.assign')
  const canValidate = useCan('candidature.validate')
  const [status, setStatus] = useState<ApplicationState | 'all'>('submitted')
  const [competition, setCompetition] = useState('')
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState('')
  const [payment, setPayment] = useState<PaymentState | ''>('')
  const [assignee, setAssignee] = useState('')
  const [decision, setDecision] = useState<'' | 'pending' | 'system'>('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string[]>([])
  const [bulkAssignee, setBulkAssignee] = useState('')

  const competitions = useQuery({
    queryKey: ['admin', 'competitions', 'options'],
    queryFn: () =>
      session.api
        .get<Paginated<Competition>>('/admin/competitions', { query: { perPage: 100 } })
        .then((r) => r.data),
    staleTime: 60_000,
  })
  const filters = { status, competition, search, payment, assignee, decision, from, to, page }
  const list = useQuery({
    queryKey: [...APPLICATIONS_KEY, filters],
    queryFn: () =>
      session.api.get<
        Paginated<AdminApplicationRow> & {
          meta: { byStatus: Partial<Record<ApplicationState, number>> }
        }
      >('/admin/applications', {
        query: {
          status: status === 'all' ? undefined : status,
          competition: competition || undefined,
          search: search || undefined,
          paymentStatus: payment || undefined,
          assignee: assignee || undefined,
          decision: decision || undefined,
          submittedFrom: from || undefined,
          submittedTo: to || undefined,
          page,
        },
      }),
    placeholderData: keepPreviousData,
  })
  // Répartition : agents habilités sur le concours filtré.
  const reviewers = useQuery({
    queryKey: ['admin', 'reviewers', competition],
    queryFn: () =>
      session.api
        .get<{ data: { id: string; name: string }[] }>('/admin/applications/reviewers', {
          query: { competition },
        })
        .then((r) => r.data),
    enabled: canAssign && Boolean(competition),
  })
  const counts = list.data?.meta.byStatus ?? {}
  const total = Object.values(counts).reduce((a, b) => a + (b ?? 0), 0)
  const reset =
    <V,>(set: (v: V) => void) =>
    (v: V) => {
      set(v)
      setPage(1)
      setSelected([])
    }

  const bulk = useMutation({
    mutationFn: (body: {
      action: 'take_charge' | 'assign' | 'validate'
      assignee?: string | null
    }) => session.api.post<BulkResult>('/admin/applications/bulk', { ...body, ids: selected }),
    onSuccess: async (res) => {
      const failed = res.results.filter((r) => !r.ok)
      if (failed.length)
        toast.error(
          `${res.done} dossier(s) traité(s), ${failed.length} refusé(s)`,
          [...new Set(failed.map((f) => f.error))].join(' ')
        )
      else toast.success(`${res.done} dossier(s) traité(s)`)
      setSelected([])
      await queryClient.invalidateQueries({ queryKey: APPLICATIONS_KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const columns: Column<AdminApplicationRow>[] = [
    {
      key: 'number',
      header: 'Matricule',
      cell: (r) => <MatriculeTag kind="candidature" value={r.applicationNumber} copyable={false} />,
    },
    {
      key: 'candidate',
      header: 'Candidat',
      cell: (r) => (
        <span>
          <strong>{r.candidate.lastName?.toUpperCase()}</strong> {r.candidate.firstName}
        </span>
      ),
    },
    { key: 'competition', header: 'Concours', cell: (r) => r.competition.reference },
    { key: 'submitted', header: 'Déposé le', cell: (r) => when(r.submittedAt) },
    {
      key: 'status',
      header: 'Dossier',
      cell: (r) => (
        <span className="stack" style={{ gap: 4, alignItems: 'flex-start' }}>
          <StatusBadge domain="application" status={r.status} />
          {r.pendingDecision && (
            <Badge variant={r.pendingDecision.bySystem ? 'sahel' : 'indigo'}>
              {r.pendingDecision.bySystem ? 'Système : ' : 'À valider : '}
              {DECISION_KINDS[r.pendingDecision.decision].toLowerCase()}
            </Badge>
          )}
          {r.complementDueAt && (
            <span className="text-muted" style={{ fontSize: 'var(--text-xs)' }}>
              Complément avant le {when(r.complementDueAt)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'payment',
      header: 'Paiement',
      cell: (r) => <StatusBadge domain="payment" status={r.paymentStatus} />,
    },
    {
      key: 'assignee',
      header: 'Agent',
      cell: (r) =>
        r.assignee ? (
          <span>{r.assignee.id === user?.id ? 'Moi' : r.assignee.name}</span>
        ) : (
          <span className="text-muted">Non attribué</span>
        ),
    },
  ]

  const submitSearch = (e: FormEvent) => {
    e.preventDefault()
    setSearch(draft.trim())
    setPage(1)
    setSelected([])
  }
  const bulkEnabled = canReview || canAssign || canValidate

  return (
    <>
      <PageHeader
        eyebrow="Dossiers"
        title="Candidatures"
        description="Recevabilité des dossiers déposés : prise en charge, contrôle des pièces, compléments, décisions proposées puis validées par une autre personne."
      />
      <Card>
        <div className="filter-row" style={{ flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <SegmentedPills
            label="État du dossier"
            value={status}
            onChange={reset(setStatus)}
            options={FILTERS.map((s) => ({
              value: s,
              label:
                s === 'all'
                  ? `Tous · ${total}`
                  : `${APPLICATION_STATE_LABELS[s]} · ${counts[s] ?? 0}`,
            }))}
          />
        </div>
        <div className="filter-row" style={{ flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <Select
            label="Concours"
            value={competition}
            onChange={(e) => reset(setCompetition)(e.target.value)}
            options={[
              { value: '', label: 'Tous les concours de ma portée' },
              ...(competitions.data ?? []).map((c) => ({
                value: c.id,
                label: `${c.reference} — ${c.title}`,
              })),
            ]}
          />
          <Select
            label="Paiement"
            value={payment}
            onChange={(e) => reset(setPayment)(e.target.value as PaymentState | '')}
            options={[
              { value: '', label: 'Tous' },
              ...PAYMENT_STATES.map((p) => ({ value: p, label: PAYMENT_STATE_LABELS[p] })),
            ]}
          />
          <Select
            label="Attribution"
            value={assignee}
            onChange={(e) => reset(setAssignee)(e.target.value)}
            options={[
              { value: '', label: 'Tous les dossiers' },
              { value: 'me', label: 'Mes dossiers' },
              { value: 'none', label: 'Non attribués' },
            ]}
          />
          <Select
            label="Décision"
            value={decision}
            onChange={(e) => {
              const value = e.target.value as '' | 'pending' | 'system'
              reset(setDecision)(value)
              // Une proposition peut viser un dossier soumis, en vérification ou à compléter.
              if (value) setStatus('all')
            }}
            options={[
              { value: '', label: 'Toutes' },
              { value: 'pending', label: 'En attente de validation' },
              { value: 'system', label: 'Proposées par le système' },
            ]}
          />
          <Input
            label="Déposé du"
            type="date"
            value={from}
            onChange={(e) => reset(setFrom)(e.target.value)}
          />
          <Input label="au" type="date" value={to} onChange={(e) => reset(setTo)(e.target.value)} />
          <form
            role="search"
            className="row"
            style={{ gap: 'var(--space-2)', alignItems: 'end' }}
            onSubmit={submitSearch}
          >
            <Input
              label="Rechercher"
              placeholder="Matricule ou nom"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button type="submit" variant="secondary" icon={<Search />}>
              Rechercher
            </Button>
          </form>
        </div>
        {selected.length > 0 && (
          <div className="bulk-bar" role="region" aria-label="Actions groupées">
            <span className="bulk-bar-count">{selected.length} dossier(s) sélectionné(s)</span>
            {canReview && (
              <Button
                size="sm"
                icon={<Hand />}
                loading={bulk.isPending && bulk.variables?.action === 'take_charge'}
                onClick={() => bulk.mutate({ action: 'take_charge' })}
              >
                Prendre en charge
              </Button>
            )}
            {canReview && user && (
              <Button
                size="sm"
                icon={<UserCheck />}
                loading={bulk.isPending && bulk.variables?.assignee === user.id}
                onClick={() => bulk.mutate({ action: 'assign', assignee: user.id })}
              >
                Me les attribuer
              </Button>
            )}
            {canAssign && competition && (
              <>
                <select
                  className="select"
                  aria-label="Attribuer à"
                  style={{ width: 'auto', minHeight: 34 }}
                  value={bulkAssignee}
                  onChange={(e) => setBulkAssignee(e.target.value)}
                >
                  <option value="">Attribuer à…</option>
                  {(reviewers.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  icon={<UserPlus />}
                  disabled={!bulkAssignee}
                  onClick={() => bulk.mutate({ action: 'assign', assignee: bulkAssignee })}
                >
                  Attribuer
                </Button>
              </>
            )}
            {canValidate && (
              <Button
                size="sm"
                variant="primary"
                icon={<CheckCheck />}
                loading={bulk.isPending && bulk.variables?.action === 'validate'}
                onClick={() => bulk.mutate({ action: 'validate' })}
              >
                Valider les décisions proposées
              </Button>
            )}
          </div>
        )}
        {list.isError && <Alert tone="danger">{errorMessage(list.error)}</Alert>}
        <Table
          columns={columns}
          rows={list.data?.data ?? []}
          rowKey={(r) => r.id}
          loading={list.isPending}
          caption="Candidatures"
          onRowClick={(r) => navigate(`/candidatures/${r.id}`)}
          selection={
            bulkEnabled
              ? {
                  selected,
                  onChange: setSelected,
                  label: (r) => `Sélectionner le dossier ${r.applicationNumber}`,
                }
              : undefined
          }
          empty={
            <Empty
              icon={<FolderOpen aria-hidden="true" />}
              title="Aucun dossier ne correspond à ces critères"
            />
          }
        />
        {list.data && list.data.meta.total > list.data.meta.perPage && (
          <Pagination
            page={page}
            perPage={list.data.meta.perPage}
            total={list.data.meta.total}
            onPageChange={(p) => {
              setPage(p)
              setSelected([])
            }}
          />
        )}
      </Card>
    </>
  )
}
